import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getIn } from 'formik';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { faPencil } from '@fortawesome/free-solid-svg-icons/faPencil';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';

import styles from './userProfileAdminDashboardStyles';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';

import EyeIcon from 'SVGs/eye';

import { newIdeaUrl, adminBecomeUser, adminUserPage } from 'buyside/util/urlHelper';

const UserProfileAdminDashboard = ({
  userProfile,
  toggleViewAsMode,
  openEditModal,
  isAdmin,
  userId,
  email,
  mobilePhone,
  workPhone
}) => {
  const checklistSpan = (title, attr) => {
    const completedClass = getIn(userProfile, attr) ? styles.jobChecklistCompletedItem : '';
    return <span className={completedClass}>{title}</span>;
  };
  const completedCurrentEmployer =
    getIn(userProfile, 'employerHistories[0].currentEmployer') &&
    getIn(userProfile, 'employerHistories[0].title') &&
    getIn(userProfile, 'employerHistories[0].employer.aumRangePresent') &&
    getIn(userProfile, 'employerHistories[0].employer.fundType');
  return (
    <div className={styles.dashboard}>
      <h4 className={styles.dashboardTitle}>Member Dashboard</h4>
      <div className={styles.dashboardSection}>
        <h5>Actions</h5>
        <div className={styles.dashboardActions}>
          <Button
            buttonType={BUTTON_TYPES.BUTTON}
            icon={EyeIcon}
            onClick={toggleViewAsMode}
            size={BUTTON_SIZES.TINY}
            text="View your profile as another user"
            type={BUTTON_TYPES.LINK}
          />
        </div>
      </div>
      <div className={styles.dashboardSection}>
        <h5>Job Vault Access</h5>
        {userProfile.user.canPostJobs ? (
          <div className={styles.checklistTitle}>
            <FontAwesomeIcon icon={faCheckCircle} color="green" />
            You may <strong>post a job</strong> in the Job Vault
          </div>
        ) : (
          <div className={styles.checklist}>
            <div className={styles.checklistTitle}>
              <FontAwesomeIcon icon={faExclamationCircle} color="gray" />
              To <strong>post a job</strong>, complete the following:
            </div>
            <div className={styles.checklistItems}>
              <Button
                disabled={!!userProfile.city}
                onClick={() => openEditModal('card')}
                size={BUTTON_SIZES.TINY}
                text={<div>{checklistSpan('Current City', 'city')}</div>}
                type={BUTTON_TYPES.PRIMARY}
              />
              <Button
                disabled={completedCurrentEmployer}
                onClick={() => openEditModal('work_experience')}
                size={BUTTON_SIZES.TINY}
                text={
                  completedCurrentEmployer ? (
                    <span className={styles.jobChecklistCompletedItem}>
                      Current Employer with title, AUM, and firm type
                    </span>
                  ) : (
                    <div>
                      {checklistSpan('Current Employer', 'employerHistories[0].currentEmployer')}
                      &nbsp;with&nbsp;
                      {checklistSpan('title', 'employerHistories[0].title')},&nbsp;
                      {checklistSpan('AUM', 'employerHistories[0].employer.aumRangePresent')},
                      and&nbsp;
                      {checklistSpan('firm type', 'employerHistories[0].employer.fundType')}
                    </div>
                  )
                }
                type={BUTTON_TYPES.PRIMARY}
              />
              <Button
                disabled={getIn(userProfile, 'schoolHistories[0]')}
                onClick={() => openEditModal('education')}
                size={BUTTON_SIZES.TINY}
                text={<div>{checklistSpan('Education', 'schoolHistories[0]')}</div>}
                type={BUTTON_TYPES.PRIMARY}
              />
            </div>
          </div>
        )}
        {userProfile.hasFilledPrivateProfile ? (
          <div className={styles.checklistTitle}>
            <FontAwesomeIcon icon={faCheckCircle} color="green" />
            You may <strong>apply to jobs</strong> in the Job Vault
          </div>
        ) : (
          <div className={styles.checklist}>
            <div className={styles.checklistTitle}>
              <FontAwesomeIcon icon={faExclamationCircle} color="gray" />
              To <strong>apply to a job</strong>, complete the following:
            </div>
            <div className={styles.checklistItems}>
              <Button
                disabled={userProfile.postedInLast3Months}
                size={BUTTON_SIZES.TINY}
                url={userProfile.postedInLast3Months ? null : newIdeaUrl()}
                text={
                  <div>
                    {checklistSpan(
                      'Publish an idea within the last 3 months',
                      'postedInLast3Months'
                    )}
                  </div>
                }
                type={BUTTON_TYPES.PRIMARY}
              />
              {userProfile.user.isHighProfile && (
                <>
                  <strong>- OR -</strong>
                  <Button
                    disabled={getIn(userProfile, 'profileAttachments[0]')}
                    onClick={() => openEditModal('research')}
                    size={BUTTON_SIZES.TINY}
                    text={
                      <div>{checklistSpan('Add a research document', 'profileAttachments[0]')}</div>
                    }
                    type={BUTTON_TYPES.PRIMARY}
                  />
                </>
              )}
              <Button
                disabled={getIn(userProfile, 'employerHistories[0]')}
                onClick={() => openEditModal('work_experience')}
                size={BUTTON_SIZES.TINY}
                text={<div>{checklistSpan('Work Experience', 'employerHistories[0]')}</div>}
                type={BUTTON_TYPES.PRIMARY}
              />
              <Button
                disabled={getIn(userProfile, 'schoolHistories[0]')}
                onClick={() => openEditModal('education')}
                size={BUTTON_SIZES.TINY}
                text={<div>{checklistSpan('Education', 'schoolHistories[0]')}</div>}
                type={BUTTON_TYPES.PRIMARY}
              />
              <Button
                disabled={!!userProfile.resume}
                onClick={() => openEditModal('resume_phone')}
                size={BUTTON_SIZES.TINY}
                text={<div>{checklistSpan('Resume', 'resume')}</div>}
                type={BUTTON_TYPES.PRIMARY}
              />
              <Button
                disabled={userProfile.hasGpa && userProfile.hasSatOrAct}
                onClick={() => openEditModal('academic_performance')}
                size={BUTTON_SIZES.TINY}
                text={
                  userProfile.hasGpa && userProfile.hasSatOrAct ? (
                    <span className={styles.jobChecklistCompletedItem}>GPA and SAT/ACT Score</span>
                  ) : (
                    <div>
                      {checklistSpan('GPA', 'hasGpa')} and&nbsp;
                      {checklistSpan('SAT/ACT Score', 'hasSatOrAct')}
                    </div>
                  )
                }
                type={BUTTON_TYPES.PRIMARY}
              />
              <Button
                disabled={userProfile.hasAllProfileAnswers}
                onClick={() => openEditModal('q_and_a')}
                size={BUTTON_SIZES.TINY}
                text={
                  <div>
                    {checklistSpan(
                      'Answer all 5 questions in the Q&A Section',
                      'hasAllProfileAnswers'
                    )}
                  </div>
                }
                type={BUTTON_TYPES.PRIMARY}
              />
            </div>
          </div>
        )}
      </div>
      {isAdmin && (
        <div className={styles.dashboardSection}>
          <h5>Contact Info (Admin)</h5>
          <div className={styles.contactInfo}>
            {email && (
              <div>
                <strong>Email</strong>: {email}
              </div>
            )}
            {mobilePhone && (
              <div>
                <strong>Mobile</strong>: {mobilePhone}
              </div>
            )}
            {workPhone && (
              <div>
                <strong>Office</strong>: {workPhone}
              </div>
            )}
          </div>
          <div className={styles.dashboardActions}>
            <Button
              faIcon={faPencil}
              size={BUTTON_SIZES.TINY}
              text="Admin Edit"
              type={BUTTON_TYPES.PRIMARY}
              url={adminUserPage(userId)}
            />
            <Button
              faIcon={faUser}
              size={BUTTON_SIZES.TINY}
              text="Become User"
              url={adminBecomeUser(userId)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileAdminDashboard;
